import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import { Icon } from './Icon'
import { Modal } from './Modal'

/** One in-app confirmation for the whole app. `window.confirm` renders as
 *  "crm.bfgusa.com says…" — a browser chrome dialog that looks like a phishing
 *  prompt, can't say which button is destructive, and blocks the page. This asks
 *  in the app's own voice: a question, what it costs, and a named action. */
interface Ask {
  title: string
  /** What actually happens. One or two plain sentences. */
  body?: ReactNode
  /** The verb on the button, e.g. "Delete it". Defaults to "Yes". */
  confirmLabel?: string
  cancelLabel?: string
  /** Red button for anything that destroys or sends. */
  danger?: boolean
}

type Confirm = (ask: Ask) => Promise<boolean>

const ConfirmContext = createContext<Confirm>(async () => true)

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<(Ask & { resolve: (ok: boolean) => void }) | null>(null)

  const confirm = useCallback<Confirm>(
    (ask) => new Promise<boolean>((resolve) => setOpen({ ...ask, resolve })),
    [],
  )

  const close = (ok: boolean) => {
    open?.resolve(ok)
    setOpen(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {open ? (
        <Modal open width={460} title={open.title} onClose={() => close(false)}
          footer={
            <>
              <span className="spacer" />
              <button className="btn" onClick={() => close(false)}>{open.cancelLabel ?? 'Cancel'}</button>
              <button className={`btn ${open.danger ? 'danger-solid' : 'primary'}`} autoFocus onClick={() => close(true)}>
                {open.danger ? <Icon name="delete" size={16} /> : null}
                {open.confirmLabel ?? 'Yes'}
              </button>
            </>
          }>
          {open.body ? <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>{open.body}</p> : undefined}
        </Modal>
      ) : null}
    </ConfirmContext.Provider>
  )
}

export const useConfirm = () => useContext(ConfirmContext)
